import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { captureAffiliateAttribution, captureCreatorReferral } from "./lib/affiliate-attribution";
import { installFetchTrace, perf } from "./lib/perf-trace";

// Install fetch tracing as early as possible so we capture every REST /
// edge-function call from the very first render. No-op unless PERF_TRACE
// is enabled (localStorage.PERF_TRACE=1 or ?perf=1).
installFetchTrace();
perf.mark("app: main.tsx boot");

// Canonical affiliate attribution capture (last paid click wins) — covers
// Refgrow, and is the source of truth for
// de-duplication at checkout, in GTM, and in the Stripe webhook.
captureAffiliateAttribution();

// First-party Content Creator referrals (`?ref=<code>`) on ANY public route.
// Server-validated, non-blocking; never affects rendering or checkout success.
//
// Referral VISIT statistics are aggregated marketing analytics only (see
// src/lib/creator-visit.ts). They can never create or remove commission.
void (async () => {
  const urlRef = new URLSearchParams(window.location.search).get("ref")?.trim().toLowerCase() || null;
  const referral = await captureCreatorReferral();
  if (!urlRef) return;
  const active = referral?.ref_code?.toLowerCase() === urlRef;
  const { recordCreatorReferralVisit } = await import("./lib/creator-visit");
  await recordCreatorReferralVisit(urlRef, active, window.location.pathname);
})();

/**
 * Homepage-scoped, paint-verified React mount gate.
 *
 * `/` ships a prerendered hero (see src/lib/prerender/static-bodies.ts).
 * `createRoot().render()` empties `#root` synchronously, so on CPU-throttled
 * mobile React's first commit can land before the browser ever paints, making
 * FCP/LCP wait for the whole React render. Instead of guessing with frames, we
 * wait for the browser to actually report `first-contentful-paint` and only
 * then hand the main thread to React.
 *
 * Scoped deliberately: every other route (/app/*, /report/*, /insights/*, auth
 * callbacks, …) keeps today's synchronous mount timing.
 */

// Single idempotent mount path — guards double mount, observer/timeout races,
// and dev/StrictMode double-execution of this module.
let mounted = false;
const trace = (label: string) => {
  if (import.meta.env.DEV) perf.mark(`boot: ${label}`);
};

const mountApp = () => {
  if (mounted) return;
  mounted = true;
  trace("react mount start");
  createRoot(document.getElementById("root")!).render(<App />);
  trace("react commit scheduled");
};

const hasFcp = () => {
  try {
    return performance
      .getEntriesByType("paint")
      .some((e) => e.name === "first-contentful-paint");
  } catch {
    return false;
  }
};

const isPrerenderedHome =
  typeof window !== "undefined" &&
  window.location.pathname.replace(/\/+$/, "") === "" &&
  !!document.querySelector("[data-prerender-home-hero]");

const paintSupported =
  typeof PerformanceObserver !== "undefined" &&
  Array.isArray((PerformanceObserver as unknown as { supportedEntryTypes?: string[] }).supportedEntryTypes)
    ? (PerformanceObserver as unknown as { supportedEntryTypes: string[] }).supportedEntryTypes.includes("paint")
    : typeof PerformanceObserver !== "undefined";

trace("bootstrap start");

if (!isPrerenderedHome) {
  mountApp();
} else if (hasFcp()) {
  // Paint already happened (e.g. back/forward, slow module execution).
  trace("existing FCP found");
  mountApp();
} else if (document.visibilityState === "hidden" || !paintSupported) {
  // A hidden document may never paint, and a browser without `paint` entries
  // can never satisfy the gate — mount immediately in both cases.
  trace(document.visibilityState === "hidden" ? "document hidden" : "paint unsupported");
  mountApp();
} else {
  /**
   * Bounded fallback: 5000 ms.
   *
   * Measured FCP for this page is ~0.8 s on desktop and 1.1–3.6 s on an
   * emulated Moto G Power over Slow 4G, so a normal foreground load reports
   * `first-contentful-paint` well inside the budget and the timeout never
   * fires — it exists only for browsers that never deliver the entry or for
   * pathological states. The app can therefore never stay unmounted.
   */
  const FALLBACK_MS = 5000;
  let observer: PerformanceObserver | null = null;
  let timer = 0;

  const finish = (reason: string) => {
    if (mounted) return;
    window.clearTimeout(timer);
    try {
      observer?.disconnect();
    } catch {
      /* noop */
    }
    observer = null;
    trace(reason);
    mountApp();
  };

  try {
    observer = new PerformanceObserver((list) => {
      if (list.getEntries().some((e) => e.name === "first-contentful-paint")) {
        finish("FCP observed");
      }
    });
    observer.observe({ type: "paint", buffered: true });
    trace("paint observer registered");
    timer = window.setTimeout(() => finish("fallback timeout fired"), FALLBACK_MS);
  } catch {
    finish("paint observer failed");
  }
}

